import AutoSizer from 'react-virtualized-auto-sizer';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
// API
import { useEnv } from '../../../../../context/env-context';
import { useAuth } from '../../../../../hooks/useAuth';
import { makeRequest } from '../../../../../utils/makeRequest';
// constants
import { REWARDS_CATEGORIES, REWARDS_STATUS_TYPE_BADGE } from '../../../../../constants/consumers';
// components
import VirtualizedList from '../../../../../components/VirtualizedList';
import Reward from './components/Reward';
import RewardDuplicateModal from './components/modals/RewardDuplicateModal';

function Rewards({ setTabSpinner }) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginConsumer, apiOriginAggregator } = useEnv();

  const [aggregators, setAggregators] = useState([]);

  const [rewardsList, setRewardsList] = useState([]);
  const [category, setCategory] = useState('');
  const [reward, setReward] = useState(null);

  const [duplicateModal, setDuplicateModal] = useState(null);

  const getAggregators = useCallback(async () => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginAggregator}/get`,
      method: 'GET',
      params: {
        sortBy: 'name',
        sortOrder: 'asc',
        statusFilter: '',
        searchText: '',
        page: 0,
        perPage: 1000000,
      },
    };

    try {
      const response = await makeRequest(config);

      setAggregators(response);
    } catch (error) {
      toast.error(`Failed getting aggregators: ${error.message}`);
    }
  }, [apiOriginAggregator, getAccessTokenSilently]);

  useEffect(() => {
    getAggregators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rewardsRequest = useCallback(
    async ({ disableSpinner = true, url, method = 'GET', payload, onSuccess }) => {
      setTabSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return null;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/rewards/${url}`,
        method,
        [method === 'POST' ? 'data' : 'params']:
          method === 'POST' ? JSON.stringify(payload) : payload,
      };

      try {
        const response = await makeRequest(config);

        if (onSuccess) {
          onSuccess(response);
        }

        return response;
      } catch (error) {
        toast.error(error.message);

        return null;
      } finally {
        if (disableSpinner) {
          setTabSpinner(false);
        }
      }
    },
    [apiOriginConsumer, getAccessTokenSilently, setTabSpinner],
  );

  const getRewards = useCallback(() => {
    rewardsRequest({
      url: 'types',
      payload: { category },
      onSuccess: response =>
        setRewardsList(response.map(x => ({ ...x, uuid: crypto.randomUUID() }))),
    });
  }, [category, rewardsRequest]);

  useEffect(() => {
    getRewards();
  }, [getRewards]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'type',
        header: 'ID',
        width: 200,
      },
      {
        key: 'name',
        header: 'Name (English)',
        width: 400,
        renderValue: v => v.en,
      },
      {
        key: 'category',
        header: 'Category',
        width: 120,
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        renderValue: status => (
          <Badge bg={REWARDS_STATUS_TYPE_BADGE[status]?.color} className="bo-inline-badge">
            {REWARDS_STATUS_TYPE_BADGE[status]?.text || status}
          </Badge>
        ),
      },
      {
        key: 'abTestingGroup',
        header: 'A/B Testing',
        width: 120,
      },
      {
        key: 'assignTo',
        header: 'Assigned To',
        width: 220,
        renderValue: assignTo =>
          assignTo.aggregators.length === 0 && assignTo.consumers.length === 0 ? (
            'All'
          ) : (
            <div>
              {assignTo.aggregators.map(x => (
                <div key={x}>{aggregators.find(agg => agg.id === x)?.name}</div>
              ))}
              {assignTo.consumers.map(x => (
                <div key={x}>{x}</div>
              ))}
            </div>
          ),
      },
      {
        key: 'edit',
        header: '',
        width: 50,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            disabled={!REWARDS_CATEGORIES.includes(row.category)}
            onClick={() =>
              rewardsRequest({
                url: 'type',
                payload: { type: row.type },
                onSuccess: response => setReward(response),
              })
            }
          >
            Edit
          </Button>
        ),
      },
      {
        key: 'duplicate',
        header: '',
        width: 80,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            disabled={!REWARDS_CATEGORIES.includes(row.category) || row.category === 'CHALLENGE'}
            onClick={() =>
              setDuplicateModal({ sourceType: row.type, newType: `duplicate-of-${row.type}` })
            }
          >
            Duplicate
          </Button>
        ),
      },
      {
        key: 'delete',
        header: '',
        width: 60,
        renderValue: (_, row) => (
          <Button
            className="bo-common-inline-button"
            onClick={() => {
              if (window.confirm('Are you sure?')) {
                rewardsRequest({
                  url: 'type/delete',
                  method: 'POST',
                  payload: { type: row.type },
                  onSuccess: () => setRewardsList(prev => prev.filter(x => x.type !== row.type)),
                });
              }
            }}
          >
            Delete
          </Button>
        ),
      },
    ],
    [aggregators, rewardsRequest],
  );

  const getRowHeight = useCallback(
    index => {
      const aggregatorsLength = rewardsList[index].assignTo.aggregators?.length || 0;
      const consumersLength = rewardsList[index].assignTo.consumers?.length || 0;

      return Math.max((aggregatorsLength + consumersLength) * 18 + 18, 36);
    },
    [rewardsList],
  );

  return (
    <>
      <AutoSizer>
        {({ height, width }) => (
          <Container style={{ width }}>
            {reward ? (
              <Reward
                reward={reward}
                setReward={setReward}
                getRewards={getRewards}
                setTabSpinner={setTabSpinner}
                aggregators={aggregators}
              />
            ) : (
              <>
                <Form.Select
                  className="bo-filter bo-w-250 mb-3"
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {REWARDS_CATEGORIES.map(x => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </Form.Select>
                <VirtualizedList
                  tableRows={rewardsList}
                  tableColumns={tableColumns}
                  getRowHeight={getRowHeight}
                  width={width - 24}
                  height={height - 72 - 54}
                  rowKey="uuid"
                />
              </>
            )}
          </Container>
        )}
      </AutoSizer>
      <RewardDuplicateModal
        setReward={setReward}
        setRewardsList={setRewardsList}
        duplicateModal={duplicateModal}
        setDuplicateModal={setDuplicateModal}
        rewardsRequest={rewardsRequest}
      />
    </>
  );
}

export default Rewards;
